/* -----------------Globals--------------- */
import { useMemo, useState } from "react";

import type { FrontendQuickStudySession } from "@features/dashboard/recommendations";
/* -----------------Helpers & Hooks--------------- */
import DurationSelector from "@memorang/ui/components/QuickStudyModal/DurationSelector";
import OptionList from "@memorang/ui/components/QuickStudyModal/OptionList";
import QuickStudyModal from "@memorang/ui/components/QuickStudyModal/QuickStudyModal";
import SkillsGrid, {
	type SkillGridItem,
} from "@memorang/ui/components/QuickStudyModal/SkillsGrid";
import type { QuickStudyTab } from "@memorang/ui/components/QuickStudyModal/types";
import RecommendedActionsList from "@memorang/ui/components/RecommendedActionsDialog/RecommendedActionsList";
import { useTranslation as useI18n } from "react-i18next";
import useSessionConfig, {
	type AdditionalParams,
} from "../hooks/useSessionConfig";

import {
	NestedExamChipsContainer,
	useHasNestedExamChips,
} from "@features/dashboard/containers/NestedExamChipsContainer";
import { diagnosticTagIds } from "@features/session/helpers/session";
import { collectDescendantIds, findTagById } from "@helpers/content/tag";
import { useAppStore } from "@hooks/useAppStore";
import type { Distribution } from "@memorang/types";
import type { MixedItemTag } from "@memorang/types";
import type { PracticeTestExam } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import { CustomListSubheader } from "@memorang/ui/components/CustomListSubheader";
import { PremiumChip } from "@memorang/ui/components/PremiumChip";
import CollapsibleSectionHeader from "@memorang/ui/components/QuickStudyModal/CollapsibleSectionHeader";
import ItemSlider from "@memorang/ui/components/SessionConfig/ItemSlider";
import ItemTypesSelector from "@memorang/ui/components/SessionConfig/ItemTypesSelector";
import DistributionFilterList, {
	type SessionConfigFilterItem,
} from "@memorang/ui/components/StudySession/DistributionFilterList";
/* -----------------Child components--------------- */
import { useProficiencyIconMap } from "@memorang/ui/hooks/useProficiencyIconMap";
/* -----------------Child components--------------- */
import DiagnosticItemsContainer from "containers/DiagnosticItemsContainer";

import FilterSections from "@memorang/ui/components/FilterSections";
import type { SectionTag } from "@memorang/ui/components/FilterSections/types";
import { questionTypeIconMap, skillIconMap } from "@memorang/ui/constants";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useDevice } from "@memorang/ui/useDevice";
import { ScrollView } from "react-native";
import {
	resolveRecommendedSessionTagIds,
	resolveSessionStartTagIds,
} from "../helpers/filter";
import { buildFlatSectionOptions } from "../helpers/flat-section-options";
import { resolveSectionBannerLabel } from "../helpers/premium-upsell";
import { TAG_TYPES } from "../helpers/session-filters";
import { resolveSummaryTopics } from "../helpers/summary-topics";
import { useDisplaySectionTags } from "../hooks/useDisplaySectionTags";
import useLoadingMessages from "../hooks/useLoadingMessages";
import {
	MASTERY_SECTION_KEY,
	collectCollapsibleSectionKeys,
	getInitialExpandedSections,
	getInitialTab,
	getSessionSummary,
	resolveSectionKey,
} from "./helpers";

type Props = {
	available: Distribution;
	additionalParams?: AdditionalParams;
	handleCreateSession: (distribution: Distribution, tags: number[]) => void;
	creatingSession?: boolean;
	handleShowPurchase: () => void;
	handleClose?: () => void;
	hasAccess?: boolean;
	exams?: PracticeTestExam[];
	distributionFilterItems: SessionConfigFilterItem[];
	showTitle?: boolean;
	studyRecommendationEnabled?: boolean;
	studyRecommendationLoading?: boolean;
	studyRecommendationSessions?: FrontendQuickStudySession[];
};

const SECTION_GAP = 16;
const STICKY_HEADER_GAP = 6;

/** `gap` is the space to the entry that follows; sticky entries need an opaque backdrop. */
type SectionEntry = {
	key: string;
	node: React.ReactNode;
	sticky: boolean;
	gap: number;
};

const getRecommendationTags = (
	additionalParams: AdditionalParams | undefined,
	fallbackTags: MixedItemTag[],
) => additionalParams?.recommendationTags ?? fallbackTags;

const PREMIUM_SKILL_ID_PREFIX = "premium-skill:";

const buildPremiumSkillGridItems = (
	premiumSkills: string[] | undefined,
): SkillGridItem[] =>
	(premiumSkills ?? []).map((title) => ({
		id: `${PREMIUM_SKILL_ID_PREFIX}${title}`,
		label: title,
		icon: skillIconMap[title] ?? "book-open-page-variant-outline",
		locked: true,
	}));

const createSkillSelectHandler =
	(
		topLevelTags: MixedItemTag[],
		handleShowPurchase: () => void,
		handleSetTagCheckboxMap: (id: string) => void,
	) =>
	(id: string) => {
		if (id.startsWith(PREMIUM_SKILL_ID_PREFIX)) {
			handleShowPurchase();
			return;
		}
		const tag = findTagById(topLevelTags, Number(id));
		if (tag?.locked) {
			handleShowPurchase();
		} else {
			handleSetTagCheckboxMap(id);
		}
	};

const SessionConfig = ({
	available,
	additionalParams,
	handleCreateSession,
	creatingSession,
	handleShowPurchase,
	hasAccess,
	exams,
	handleClose,
	distributionFilterItems,
	showTitle,
	studyRecommendationEnabled = false,
	studyRecommendationLoading = false,
	studyRecommendationSessions = [],
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: na
}: Props) => {
	const hideRecommendedTab = useAppStore(
		(state) => state.app.sessionConfig?.hideRecommendedTab,
	);
	const showLearningModes = useAppStore(
		(state) => state.app.sessionConfig?.showLearningMode,
	);
	const maxFilterDepth = useAppStore(
		(state) => state.app.sessionConfig?.maxFilterDepth ?? 1,
	);
	const { t, i18n } = useI18n();
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const [activeTab, setActiveTab] = useState<QuickStudyTab>(() =>
		getInitialTab(additionalParams),
	);
	const [selectedRecommendedId, setSelectedRecommendedId] = useState<string>();
	const showDistributionFilters = true;
	const showItemsSlider = useAppStore(
		(state) => state.app.sessionConfig?.showItemsSlider,
	);
	const showDurationSelection = useAppStore(
		(state) => state.app.sessionConfig?.showDurationSelection,
	);
	const hideTotalItemCounts = useAppStore(
		(state) => state.app.hideTotalItemCounts,
	);
	const proficiencyIconMap = useProficiencyIconMap();
	const hasNestedExamChips = useHasNestedExamChips();

	const {
		handleSetTagCheckboxMap,
		selectedTagCheckboxMap,
		numRequiredItems,
		requiredDistribution,
		selectedLearningMode,
		handleSetLearningMode,
		finalAvailableDist,
		selectedDistributionCheckboxMap,
		handleDistributionCheckboxState,
		availableItems,
		numTotalItemsFiltered,
		handleSliderChange,
		selectedDurationMinutes,
		handleDurationChange,
		unavailableQuestionTypeIds,
		structuredTagCounts,
	} = useSessionConfig({
		availableDistribution: available,
		additionalParams: additionalParams || {},
		isSessionCreationInProgress: creatingSession,
		showLearningMode: showLearningModes,
		showDistributionFilters,
		distributionFilterItems,
	});

	const ctaText = useLoadingMessages(Boolean(creatingSession));
	const topLevelTags = useMemo(
		() =>
			additionalParams?.sectionTags?.flatMap((section) => section.tags) ?? [],
		[additionalParams?.sectionTags],
	);
	const recommendationTags = getRecommendationTags(
		additionalParams,
		topLevelTags,
	);
	const recommendedSessions =
		studyRecommendationEnabled && !studyRecommendationLoading
			? studyRecommendationSessions
			: [];
	const effectiveSelectedRecommendedId =
		selectedRecommendedId ?? recommendedSessions[0]?.id;
	const selectedRecommendedSession = recommendedSessions.find(
		(session) => session.id === effectiveSelectedRecommendedId,
	);
	const showRecommendedTab =
		recommendedSessions.length > 0 && !hideRecommendedTab;
	const effectiveActiveTab = showRecommendedTab ? activeTab : "custom";

	const { filteredSectionTags, displaySectionTags } = useDisplaySectionTags({
		sectionTags: additionalParams?.sectionTags,
		selectedLearningMode,
		structuredTagCounts,
		unavailableQuestionTypeIds,
	});

	const [expandedSections, setExpandedSections] = useState<Set<string>>(() =>
		getInitialExpandedSections(
			collectCollapsibleSectionKeys({
				sectionTags: displaySectionTags,
				showDistributionFilters,
			}),
		),
	);
	const toggleSectionExpanded = (key: string) => {
		setExpandedSections((current) => {
			const next = new Set(current);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	};

	const selectedSkillId = displaySectionTags
		?.find((section) => section.tags[0]?.type === TAG_TYPES.skill)
		?.tags.find((tag) => selectedTagCheckboxMap?.[tag.id])?.id;

	const filterSelectedDiagnosticTags =
		selectedTagCheckboxMap &&
		Object.keys(selectedTagCheckboxMap).filter(
			(value) =>
				selectedTagCheckboxMap[value] &&
				Object.values(diagnosticTagIds).includes(Number(value)),
		);

	const isDiagnosticTagSelected =
		filterSelectedDiagnosticTags && filterSelectedDiagnosticTags.length > 0;

	const handleStartSession = () => {
		if (creatingSession) {
			return;
		}
		if (effectiveActiveTab === "recommended" && selectedRecommendedSession) {
			handleCreateSession(
				selectedRecommendedSession.distribution,
				resolveRecommendedSessionTagIds(
					selectedRecommendedSession.tagIds,
					recommendationTags,
					additionalParams?.freeTagIds,
				),
			);
			return;
		}
		const finalTagIds = resolveSessionStartTagIds(
			selectedTagCheckboxMap,
			displaySectionTags ?? additionalParams?.sectionTags,
			additionalParams?.hasAccess,
			additionalParams?.freeTagIds,
		);
		handleCreateSession(requiredDistribution, finalTagIds);
	};

	const handleSkillSelect = createSkillSelectHandler(
		topLevelTags,
		handleShowPurchase,
		handleSetTagCheckboxMap,
	);

	const showSummary = numRequiredItems > 0;
	const customSummaryText = useMemo(() => {
		if (!showSummary) {
			return undefined;
		}
		const selectedProgresses = selectedDistributionCheckboxMap
			? distributionFilterItems
					.filter(({ distKey }) => selectedDistributionCheckboxMap[distKey])
					.map(({ distKey }) => distKey)
			: [];

		const availableTopics = resolveSummaryTopics({
			sectionTags: filteredSectionTags,
			maxFilterDepth,
		});
		const selectedTopics = selectedTagCheckboxMap
			? Object.keys(selectedTagCheckboxMap)
					.filter((tagId) => selectedTagCheckboxMap[tagId])
					.map((tagId) => findTagById(topLevelTags, Number(tagId))?.title)
					.filter((title): title is string => Boolean(title))
			: [];

		return getSessionSummary({
			progresses: selectedProgresses,
			totalProgressCount: distributionFilterItems.length,
			itemCount: numRequiredItems,
			selectedTopics,
			availableTopics,
			t,
			language: i18n.language,
		});
	}, [
		distributionFilterItems,
		filteredSectionTags,
		i18n.language,
		maxFilterDepth,
		numRequiredItems,
		selectedDistributionCheckboxMap,
		selectedTagCheckboxMap,
		showSummary,
		t,
		topLevelTags,
	]);
	const selectedSummary = selectedRecommendedSession?.description;
	const summaryText =
		effectiveActiveTab === "recommended" ? selectedSummary : customSummaryText;
	const startDisabled =
		creatingSession ||
		(effectiveActiveTab === "recommended"
			? !selectedRecommendedSession
			: !numRequiredItems);

	const entries: SectionEntry[] = [];

	const pushBlock = (key: string, node: React.ReactNode) => {
		entries.push({ key, node, sticky: false, gap: SECTION_GAP });
	};

	const upsellChip = (
		<PremiumChip
			variant="premium"
			onPress={handleShowPurchase}
			disabled={creatingSession}
		/>
	);

	const pushCollapsibleSection = ({
		key,
		title,
		selectedCount,
		accessory,
		body,
	}: {
		key: string;
		title: string;
		selectedCount: number;
		accessory?: React.ReactNode;
		body: React.ReactNode;
	}) => {
		const expanded = expandedSections.has(key);
		entries.push({
			key: `${key}-header`,
			sticky: true,
			gap: expanded ? STICKY_HEADER_GAP : SECTION_GAP,
			node: (
				<CollapsibleSectionHeader
					title={title}
					selectedCount={selectedCount}
					accessory={accessory}
					expanded={expanded}
					onToggleExpanded={() => toggleSectionExpanded(key)}
				/>
			),
		});
		if (expanded) {
			entries.push({
				key: `${key}-body`,
				sticky: false,
				gap: SECTION_GAP,
				node: body,
			});
		}
	};

	const pushFlatSectionEntries = (section: SectionTag, isQT: boolean) => {
		const bannerLabel = resolveSectionBannerLabel({
			section,
			premiumTagsByType: additionalParams?.premiumTagsByType,
			generalTags: additionalParams?.premiumStudyPackTags,
			selectedSkillId,
			t,
			language: i18n.language,
		});
		const rowIconMap = isQT ? questionTypeIconMap : proficiencyIconMap;
		const rowDefaultIcon = isQT ? "text-box-outline" : "school-outline";

		const { options } = buildFlatSectionOptions({
			tags: section.tags,
			selectedTagCheckboxMap,
			hideTotalItemCounts,
			rowIconMap,
			rowDefaultIcon,
			t,
		});

		pushCollapsibleSection({
			key: resolveSectionKey(section),
			title: section.title,
			selectedCount: options.filter((option) => option.checked).length,
			accessory: bannerLabel ? upsellChip : undefined,
			body: (
				<OptionList
					options={options}
					unavailableTooltip={t("sessionConfig.noItemsTooltip")}
					bannerLabel={bannerLabel}
					onPressBanner={handleShowPurchase}
					disabled={creatingSession || isDiagnosticTagSelected}
					onToggle={handleSetTagCheckboxMap}
				/>
			),
		});
	};

	if (hasNestedExamChips) {
		pushBlock(
			"exam-chips",
			<NestedExamChipsContainer disabled={creatingSession} variant="radio" />,
		);
	}

	if (!showLearningModes) {
		pushBlock(
			"diagnostics",
			<DiagnosticItemsContainer
				exams={exams}
				hasAccess={hasAccess}
				handleClose={handleClose}
				handleShowPurchase={handleShowPurchase}
				paddingHorizontal={16}
				isSessionConfig
				disableAll={creatingSession}
			/>,
		);
	}

	if (
		showLearningModes &&
		!filteredSectionTags?.some(
			(s) => s.tags[0]?.type === TAG_TYPES.questionType,
		)
	) {
		pushBlock(
			"item-types",
			<ItemTypesSelector
				selectedLearningMode={selectedLearningMode}
				handleSetLearningMode={handleSetLearningMode}
				numItemsMap={
					additionalParams?.numItemsMap ?? {
						numQuestions: 0,
						numFlashcards: 0,
						numEssays: 0,
					}
				}
				disableAll={creatingSession}
			/>,
		);
	}

	if (showDurationSelection) {
		pushBlock(
			"duration",
			<Box gap={8}>
				<CustomListSubheader
					enableGutters={false}
					style={{ paddingVertical: 0 }}
				>
					{t("sessionConfig.quickStudy.durationSelector.title")}
				</CustomListSubheader>
				<DurationSelector
					selected={selectedDurationMinutes}
					disabled={creatingSession}
					formatLabel={(minutes) =>
						t("sessionConfig.quickStudy.duration", { minutes })
					}
					onSelect={handleDurationChange}
				/>
			</Box>,
		);
	}

	if (showItemsSlider && !showDurationSelection) {
		pushBlock(
			"items-slider",
			<ItemSlider
				label={t("sessionConfig.sessionLength")}
				availableItems={availableItems}
				requiredItems={numRequiredItems}
				disabled={creatingSession}
				numTotalItemsFiltered={numTotalItemsFiltered}
				hideTotalItemCounts={hideTotalItemCounts}
				handleSliderChange={handleSliderChange}
				testID="session-length-slider"
				gutterless
			/>,
		);
	}

	for (const section of displaySectionTags ?? []) {
		const tagType = section.tags[0]?.type;
		const isQT = tagType === TAG_TYPES.questionType;
		const isSkill = tagType === TAG_TYPES.skill;
		const isProficiency = tagType === TAG_TYPES.proficiency;
		const hasNestedTags = section.tags.some((tag) => tag.children?.length);

		if (isSkill && !hasNestedTags) {
			const existingTitles = new Set(
				section.tags.map((tag) => tag.title.toLowerCase()),
			);
			const premiumSkillTitles = additionalParams?.premiumSkills?.length
				? additionalParams.premiumSkills
				: additionalParams?.premiumTagsByType?.[TAG_TYPES.skill]?.map(
						(tag) => tag.title,
					);
			const premiumSkillItems = buildPremiumSkillGridItems(
				premiumSkillTitles,
			).filter((skill) => !existingTitles.has(skill.label.toLowerCase()));
			const skillItems: SkillGridItem[] = [
				...section.tags.map((tag) => ({
					id: String(tag.id),
					label: tag.title,
					icon: skillIconMap[tag.title] ?? "book-open-page-variant-outline",
					disabled: (tag.numItems ?? 0) === 0,
					locked: Boolean(tag.locked),
				})),
				...premiumSkillItems,
			];
			const selectedSkillTag = section.tags.find(
				(tag) => selectedTagCheckboxMap?.[tag.id],
			);
			pushBlock(
				`section:${section.title}`,
				<Box gap={8}>
					<CustomListSubheader
						enableGutters={false}
						style={{ paddingVertical: 0 }}
					>
						{section.title}
					</CustomListSubheader>
					<SkillsGrid
						skills={skillItems}
						selectedId={
							selectedSkillTag ? String(selectedSkillTag.id) : undefined
						}
						disabled={creatingSession || isDiagnosticTagSelected}
						onSelect={handleSkillSelect}
					/>
				</Box>,
			);
			continue;
		}

		if ((isQT || isProficiency) && !hasNestedTags) {
			pushFlatSectionEntries(section, isQT);
			continue;
		}

		const iconMap = isQT
			? questionTypeIconMap
			: isSkill
				? skillIconMap
				: isProficiency
					? proficiencyIconMap
					: undefined;
		const sectionTagIds = section.tags.flatMap((tag) => [
			tag.id,
			...collectDescendantIds(tag),
		]);
		const allLocked = !hasAccess && section.tags.every((tag) => tag.locked);
		pushCollapsibleSection({
			key: resolveSectionKey(section),
			title: section.title,
			selectedCount: sectionTagIds.filter((id) => selectedTagCheckboxMap?.[id])
				.length,
			accessory: allLocked ? upsellChip : undefined,
			body: (
				<FilterSections
					disableAll={creatingSession || isDiagnosticTagSelected}
					selectedCheckboxMap={selectedTagCheckboxMap}
					onSelectItem={(distKey, locked) => {
						if (locked) {
							handleShowPurchase();
						} else {
							handleSetTagCheckboxMap(distKey);
						}
					}}
					hasAccess={hasAccess}
					sectionTags={[section]}
					maxFilterDepth={maxFilterDepth}
					iconMap={iconMap}
					itemVariant={isSkill ? "radio" : "checkbox"}
					hideItemCounts={
						Boolean(hideTotalItemCounts) && (isQT || isProficiency)
					}
					disabledTooltip={t("sessionConfig.noItemsTooltip")}
					hideSectionHeaders
				/>
			),
		});
	}

	if (showDistributionFilters) {
		pushCollapsibleSection({
			key: MASTERY_SECTION_KEY,
			title: t("sessionConfig.mastery"),
			selectedCount: distributionFilterItems.filter(
				({ distKey }) => selectedDistributionCheckboxMap?.[distKey],
			).length,
			body: (
				<DistributionFilterList
					distribution={finalAvailableDist}
					hasAccess={hasAccess || Boolean(additionalParams?.freeTagIds?.length)}
					disableAll={creatingSession}
					selectedCheckboxMap={selectedDistributionCheckboxMap}
					handleSelectCheckbox={handleDistributionCheckboxState}
					distributionFilterItems={distributionFilterItems}
					handleShowPurchase={handleShowPurchase}
					hideTotalItemCounts={hideTotalItemCounts}
				/>
			),
		});
	}

	const customTabContent = (
		<ScrollView
			showsVerticalScrollIndicator={false}
			stickyHeaderIndices={entries.flatMap((entry, index) =>
				entry.sticky ? [index] : [],
			)}
			contentContainerStyle={{
				paddingHorizontal: isMobile ? 16 : 24,
				paddingVertical: 16,
			}}
		>
			{entries.map((entry, index) => (
				<Box
					key={entry.key}
					paddingBottom={index === entries.length - 1 ? 0 : entry.gap}
					backgroundColor={entry.sticky ? theme.colors.surface : undefined}
				>
					{entry.node}
				</Box>
			))}
		</ScrollView>
	);

	return (
		<QuickStudyModal
			title={showTitle ? t("sessionConfig.quickStudy.title") : undefined}
			onClose={handleClose}
			activeTab={effectiveActiveTab}
			onChangeTab={setActiveTab}
			recommendedLabel={t("sessionConfig.quickStudy.recommended")}
			customLabel={t("sessionConfig.quickStudy.custom")}
			summaryText={summaryText}
			startLabel={ctaText}
			startDisabled={startDisabled}
			starting={creatingSession}
			onStart={handleStartSession}
			showStartButton={
				effectiveActiveTab === "recommended"
					? !(!hasAccess && exams?.length)
					: true
			}
			showTabs={showRecommendedTab}
			scrollEnabled={effectiveActiveTab !== "custom"}
		>
			{effectiveActiveTab === "recommended" ? (
				<RecommendedActionsList
					actions={recommendedSessions}
					selectedId={effectiveSelectedRecommendedId}
					disabled={creatingSession}
					helperText={t("sessionConfig.quickStudy.selectHelper")}
					emptyText={t("sessionConfig.quickStudy.emptyRecommended")}
					onSelect={setSelectedRecommendedId}
				/>
			) : (
				customTabContent
			)}
		</QuickStudyModal>
	);
};

export default SessionConfig;
